import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Phone, ArrowLeft, User, Paperclip, Image, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/FloatingChat.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_AVATAR = "https://customer-assets.emergentagent.com/job_30e70ec7-9b70-4809-beca-18d857724f80/artifacts/ri9zkzxm_photo_2026-03-03_22-25-58.jpg";

// Phone validation - must be proper format with country code
const validatePhone = (phone) => {
  // Remove spaces and dashes
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // Must start with + and have 10-15 digits
  const phoneRegex = /^\+[0-9]{10,15}$/;
  return phoneRegex.test(cleaned);
};

const FloatingChat = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  // Listen for toggle event from bottom nav
  useEffect(() => {
    const handleToggle = () => setIsOpen(prev => !prev);
    window.addEventListener('toggle-chat', handleToggle);
    return () => window.removeEventListener('toggle-chat', handleToggle);
  }, []);

  // Hide bottom nav when chat is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }
    return () => document.body.classList.remove('chat-open');
  }, [isOpen]);
  const [step, setStep] = useState('phone'); // 'phone', 'chat'
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [conversation, setConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load saved phone from localStorage
  useEffect(() => {
    const savedPhone = localStorage.getItem('chatPhone');
    const savedName = localStorage.getItem('chatName');
    if (savedPhone) {
      setPhone(savedPhone);
      setName(savedName || '');
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Auto-refresh messages when in chat mode
  useEffect(() => {
    if (step === 'chat' && phone && isOpen) {
      // Refresh every 3 seconds
      refreshIntervalRef.current = setInterval(() => {
        fetchConversationSilent(phone);
      }, 3000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [step, phone, isOpen]);

  // Silent fetch (no loading indicator)
  const fetchConversationSilent = async (phoneNumber) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages/phone/${encodeURIComponent(phoneNumber)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          setConversation(data);
        }
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
    }
  };

  // Fetch conversation when chat opens
  const fetchConversation = async (phoneNumber) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages/phone/${encodeURIComponent(phoneNumber)}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // data could be null (no conversation) or the conversation object
        if (data && data.id) {
          setConversation(data);
          if (data.name) {
            setName(data.name);
            localStorage.setItem('chatName', data.name);
          }
        } else {
          // No existing conversation
          setConversation(null);
        }
        setStep('chat');
        localStorage.setItem('chatPhone', phoneNumber);
      } else {
        // Error response - still allow to proceed
        setConversation(null);
        setStep('chat');
        localStorage.setItem('chatPhone', phoneNumber);
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      // Allow to proceed even if fetch fails
      setConversation(null);
      setStep('chat');
      localStorage.setItem('chatPhone', phoneNumber);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t('pleaseEnterName') || 'Please enter your name');
      return;
    }
    if (!phone.trim()) {
      setError(t('pleaseEnterPhone') || 'Please enter your phone number');
      return;
    }
    // Validate phone format
    if (!validatePhone(phone)) {
      setError(t('invalidPhoneFormat') || 'Please enter a valid phone number with country code (e.g., +971 50 123 4567)');
      return;
    }
    localStorage.setItem('chatName', name);
    fetchConversation(phone);
  };

  // Handle file attachment
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('fileTooLarge') || 'File is too large. Maximum size is 5MB.');
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('invalidFileType') || 'Invalid file type. Please upload an image or PDF.');
      return;
    }

    setAttachment(file);
    setError('');

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !attachment) return;

    setIsSending(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('message', newMessage || (attachment ? `[Attachment: ${attachment.name}]` : ''));
      
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setNewMessage('');
        setAttachment(null);
        setAttachmentPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh conversation
        fetchConversation(phone);
      } else {
        setError(data.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setConversation(null);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  // Build messages array from conversation
  const getMessages = () => {
    if (!conversation) return [];
    
    const messages = [];
    
    // Add the original message
    messages.push({
      id: conversation.id,
      type: 'user',
      text: conversation.message,
      time: conversation.createdAt,
      attachment: conversation.attachment,
      attachmentName: conversation.attachmentName
    });

    // Add replies (both admin and user)
    if (conversation.replies && conversation.replies.length > 0) {
      conversation.replies.forEach(reply => {
        messages.push({
          id: reply.id,
          type: reply.isAdmin ? 'admin' : 'user',
          text: reply.text,
          time: reply.createdAt,
          attachment: reply.attachment,
          attachmentName: reply.attachmentName
        });
      });
    }

    return messages.sort((a, b) => new Date(a.time) - new Date(b.time));
  };

  return (
    <div className="floating-chat-container">
      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window chat-window-conversation">
          {/* Header */}
          <div className="chat-header">
            {step === 'chat' && (
              <button className="chat-back-btn" onClick={handleBack}>
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="chat-header-info">
              <img src={SUPPORT_AVATAR} alt="Support" className="support-avatar-img" />
              <span>{step === 'phone' ? t('startChat') : t('registanSupport')}</span>
            </div>
            <button className="chat-close-btn" onClick={handleClose}>
              <X size={20} />
            </button>
          </div>

          {/* Phone Entry Step */}
          {step === 'phone' && (
            <form className="chat-form" onSubmit={handlePhoneSubmit}>
              <div className="chat-welcome">
                <img src={SUPPORT_AVATAR} alt="Support" className="welcome-avatar-img" />
                <h3>{t('chatWelcome')}</h3>
                <p>{t('chatWelcomeDesc')}</p>
              </div>

              <div className="chat-form-group">
                <label>{t('yourName')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('enterYourName')}
                  data-testid="chat-name-input"
                />
              </div>

              <div className="chat-form-group">
                <label>{t('phoneNumber')}</label>
                <div className="phone-input-wrapper">
                  <Phone size={18} />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+971 XX XXX XXXX"
                    data-testid="chat-phone-input"
                  />
                </div>
              </div>

              {error && <div className="chat-error">{error}</div>}

              <button 
                type="submit" 
                className="chat-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? t('loadingChat') : t('startChatBtn')}
              </button>
            </form>
          )}

          {/* Chat Step */}
          {step === 'chat' && (
            <>
              {/* Messages Area */}
              <div className="chat-messages">
                {isLoading ? (
                  <div className="chat-loading">{t('loadingMessages')}</div>
                ) : (
                  <>
                    {getMessages().length === 0 ? (
                      <div className="chat-empty">
                        <p>{t('noMessagesYet')}</p>
                      </div>
                    ) : (
                      getMessages().map((msg, index) => (
                        <div key={msg.id || index} className={`chat-message ${msg.type}`}>
                          {msg.type === 'admin' && (
                            <div className="admin-label">
                              <User size={14} />
                              <span>Ramizjan</span>
                            </div>
                          )}
                          <div className="message-bubble">
                            {msg.attachment && (
                              <div className="message-attachment">
                                {msg.attachment.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <img 
                                    src={`${BACKEND_URL}${msg.attachment}`} 
                                    alt={msg.attachmentName || 'Attachment'} 
                                    className="message-attachment-image"
                                    onClick={() => window.open(`${BACKEND_URL}${msg.attachment}`, '_blank')}
                                  />
                                ) : (
                                  <a 
                                    href={`${BACKEND_URL}${msg.attachment}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="message-attachment-file"
                                  >
                                    <FileText size={20} />
                                    <span>{msg.attachmentName || 'Download file'}</span>
                                  </a>
                                )}
                              </div>
                            )}
                            {msg.text && <p>{msg.text}</p>}
                            <span className="message-time">{formatTime(msg.time)}</span>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <form className="chat-input-form" onSubmit={handleSendMessage}>
                {error && <div className="chat-error">{error}</div>}
                
                {/* Attachment Preview */}
                {attachment && (
                  <div className="attachment-preview">
                    {attachmentPreview ? (
                      <img src={attachmentPreview} alt="Preview" className="attachment-image" />
                    ) : (
                      <div className="attachment-file">
                        <FileText size={24} />
                        <span>{attachment.name}</span>
                      </div>
                    )}
                    <button type="button" className="remove-attachment" onClick={removeAttachment}>
                      <X size={16} />
                    </button>
                  </div>
                )}
                
                <div className="chat-input-wrapper">
                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                  />
                  
                  {/* Attachment button */}
                  <button 
                    type="button" 
                    className="chat-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSending}
                    title={t('attachFile') || 'Attach file'}
                  >
                    <Paperclip size={20} />
                  </button>
                  
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('typeMessage')}
                    disabled={isSending}
                    data-testid="chat-message-input"
                  />
                  <button 
                    type="submit" 
                    className="chat-send-btn"
                    disabled={isSending || (!newMessage.trim() && !attachment)}
                  >
                    <Send size={20} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {/* Floating Button */}
      <button 
        className={`floating-chat-btn ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="floating-chat-btn"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};

export default FloatingChat;
